
// src/pages/Shipping.jsx

import React, { useState, useEffect } from 'react'
import Select from 'react-select'
import { allCountries } from 'country-telephone-data'
import Headers from '../components/Headers'
import Footer from '../components/Footer'
import LocationSelector from '../components/LocationSelector'
import { useLocation, Link, useNavigate } from 'react-router-dom'
import { MdOutlineKeyboardArrowRight } from 'react-icons/md'
import { useDispatch, useSelector } from 'react-redux'

import { place_order, create_order } from '../store/reducers/orderReducer'
import {
  get_shipping_address,
  get_all_addresses,
  add_new_address,
  update_shipping_address,
  get_selected_address,
  set_selected_address,
  get_one_day_quote,
  create_borzo_order        // ← swapped in
} from '../store/reducers/customerShippingReducer'
import orderjpg from '../assets/banner/order.jpg'
import { get_standard_quote } from '../store/reducers/shiprocketReducer'

// helper: convert ISO2 to emoji flag
const flagEmoji = iso2 =>
  iso2.toUpperCase().replace(/./g, c =>
    String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65)
  )

// build react-select options with flags + dial codes
const countryOptions = allCountries.map(c => ({
  value: `+${c.dialCode}`,
  label: `${flagEmoji(c.iso2)} +${c.dialCode} ${c.name}`
}))

const addressFields = [
  { label: 'Name', name: 'name', placeholder: 'Name' },
  { label: 'Address', name: 'address', placeholder: 'House no / building / street / area' },
  { label: 'Phone', name: 'phone', placeholder: 'Phone' },
  { label: 'Post', name: 'post', placeholder: 'Post' },
  { label: 'Province', name: 'province', placeholder: 'Province' },
  { label: 'City', name: 'city', placeholder: 'City' },
  { label: 'Area', name: 'area', placeholder: 'Area' },
  { label: 'Save As', name: 'saveAs', placeholder: 'Home, Office, etc.' }
]

function validateAddress({ name, address, phone, post, province, city, area, countryCode }) {
  const errors = {}
  if (!name) errors.name = 'Required'
  if (!address) errors.address = 'Required'
  if (!countryCode) errors.countryCode = 'Select country code'
  if (!phone) errors.phone = 'Required'
  else if (!/^\d{10}$/.test(phone)) errors.phone = 'Must be 10 digits'
  if (!post) errors.post = 'Required'
  else if (!/^\d{6}$/.test(post)) errors.post = 'Must be 6 digits'
  if (!province) errors.province = 'Required'
  if (!city) errors.city = 'Required'
  if (!area) errors.area = 'Required'
  return errors
}

const AddressForm = ({ values, onChange, onSubmit, buttonLabel, errors, formErrorMessage }) => (
  <form onSubmit={onSubmit} className="space-y-4">
    {addressFields.map(field => {
      if (['province', 'city', 'area'].includes(field.name)) return null
      const err = errors[field.name]

      if (field.name === 'phone') {
        return (
          <div key="phone" className="flex items-start gap-2">
            <label className="w-20 pt-2">{field.label}*</label>
            <div className="flex gap-2 flex-1">
              <div className="w-32">
                <Select
                  options={countryOptions}
                  value={countryOptions.find(o => o.value === values.countryCode)}
                  onChange={opt => onChange({ target: { name: 'countryCode', value: opt.value } })}
                  isSearchable
                  styles={{
                    control: base => ({ ...base, minHeight: '40px' }),
                    menu: base => ({ ...base, zIndex: 9999 })
                  }}
                />
                {errors.countryCode && <p className="text-red-500 text-xs">{errors.countryCode}</p>}
              </div>
              <input
                name="phone"
                value={values.phone}
                onChange={onChange}
                placeholder={field.placeholder}
                className={`flex-1 px-3 py-2 border rounded ${err ? 'border-red-500' : 'border-gray-300'}`}
              />
            </div>
            {err && <p className="text-red-500 text-xs">{err}</p>}
          </div>
        )
      }

      if (field.name === 'post') {
        return (
          <div key="post">
            <label>{field.label}*</label>
            <input
              name="post"
              value={values.post}
              onChange={onChange}
              placeholder={field.placeholder}
              maxLength={6}
              className={`w-full px-3 py-2 border rounded ${err ? 'border-red-500' : 'border-gray-300'}`}
            />
            {err && <p className="text-red-500 text-xs">{err}</p>}
          </div>
        )
      }

      return (
        <div key={field.name}>
          <label>{field.label}*</label>
          <input
            name={field.name}
            value={values[field.name]}
            onChange={onChange}
            placeholder={field.placeholder}
            className={`w-full px-3 py-2 border rounded ${err ? 'border-red-500' : 'border-gray-300'}`}
          />
          {err && <p className="text-red-500 text-xs">{err}</p>}
        </div>
      )
    })}

    <LocationSelector values={values} onChange={onChange} errors={errors} />

    {formErrorMessage && <p className="text-red-500">{formErrorMessage}</p>}

    <button
      type="submit"
      className="px-6 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
    >
      {buttonLabel}
    </button>
  </form>
)

const Shipping = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { userInfo } = useSelector(s => s.auth)
  const {
    shippingAddress,
    shippingAddresses,
    selectedAddress,
    oneDayQuote: { quoteId: oneDayQuoteId, price: oneDayPrice },
    oneDayLoading
  } = useSelector(s => s.customer)
  const {
    standardQuote,
    standardQuoteLoading,
    standardQuoteError
  } = useSelector(s => s.shiprocket)

  // pull out our 4 Shiprocket fields (or default to an empty object)
  const {
    courier_company_id,
    courier_name,
    quotedPrice,
    transit_time,
    estimated_delivery_days
  } = standardQuote || {}


  const { state: { products, price, shipping_fee, items } = {} } = useLocation()

  // ─── state ─────────────────────────────────────────
  const [packingCharge] = useState(() => Math.floor(Math.random() * 31) + 30)
  const [oldSaved, setOldSaved] = useState(false)
  const [oldAddressState, setOldAddressState] = useState({
    name: '', address: '', phone: '', countryCode: '', post: '', province: '', city: '', area: '', saveAs: ''
  })
  const [allAddresses, setAllAddresses] = useState([])
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [showNewForm, setShowNewForm] = useState(false)
  const [showEditForm, setShowEditForm] = useState(false)

  const [newAddress, setNewAddress] = useState({ ...oldAddressState })
  const [editAddress, setEditAddress] = useState({})

  const [oldErrors, setOldErrors] = useState({})
  const [newErrors, setNewErrors] = useState({})
  const [editErrors, setEditErrors] = useState({})

  const [useOneDay, setUseOneDay] = useState(false)
  const [showOneDayOption, setShowOneDayOption] = useState(false)

  // ─── derived ──────────────────────────────────────
  const currentAddress = allAddresses[selectedIndex] || null
  const displayedOneDayPrice = oneDayPrice != null
    ? Math.ceil(oneDayPrice * 1.15) // 15% markup
    : null

  // our standard‐delivery fallback
  const displayedStandardPrice = quotedPrice != null
    ? Math.ceil(quotedPrice * 1.3)
    : null

  // ─── load on mount ─────────────────────────────────
  useEffect(() => {
    if (userInfo?.id) {
      dispatch(get_shipping_address(userInfo.id))
      dispatch(get_all_addresses(userInfo.id))
      dispatch(get_selected_address(userInfo.id))
    }
  }, [userInfo, dispatch])
  useEffect(() => {
    if (!useOneDay && currentAddress?.post) {
      console.log("👉 Calling Shiprocket quote with:", {
        pickup_pincode: process.env.REACT_APP_WAREHOUSE_PINCODE,
        delivery_pincode: currentAddress.post,
        weight: 1
      })

      dispatch(get_standard_quote({
        pickup_pincode: process.env.REACT_APP_WAREHOUSE_PINCODE,
        delivery_pincode: currentAddress.post,
        weight: 1
      }))
        .unwrap()
        .then(response => {
          // this is *exactly* what your controller returned
          console.log("✅ Shiprocket quote response:", response)
          localStorage.setItem(
            'standardEstimatedDeliveryDays',
            response.estimated_delivery_days
          )
        })
        .catch(err => {
          console.error("❌ Shiprocket quote failed:", err)
        })
    }
  }, [useOneDay, currentAddress?.post, dispatch])


  // stash primary into oldAddressState
  useEffect(() => {
    if (shippingAddress?._id) {
      setOldAddressState(shippingAddress)
      setOldSaved(true)
    }
  }, [shippingAddress])

  // combine into list + maintain selection
  useEffect(() => {
    let combo = shippingAddresses.map(a => ({ ...a }))
    if (oldSaved && oldAddressState._id) {
      combo = combo.filter(a => a._id !== oldAddressState._id)
      combo.unshift(oldAddressState)
    }
    setAllAddresses(combo)

    if (selectedAddress?._id) {
      const idx = combo.findIndex(a => a._id === selectedAddress._id)
      if (idx >= 0) setSelectedIndex(idx)
    } else if (selectedIndex >= combo.length) {
      setSelectedIndex(0)
    }
  }, [oldSaved, oldAddressState, shippingAddresses, selectedAddress])

  // ─── handlers ───────────────────────────────────────
  const handleOldInput = e => {
    const { name, value } = e.target
    setOldAddressState(s => ({ ...s, [name]: value }))
    setOldErrors(e => ({ ...e, [name]: '' }))
  }
  const saveOldAddress = e => {
    e.preventDefault()
    const errs = validateAddress(oldAddressState)
    if (Object.keys(errs).length) return setOldErrors(errs)

    const action = oldSaved
      ? update_shipping_address({ addressId: oldAddressState._id, addressData: oldAddressState })
      : add_new_address({ customerId: userInfo.id, addressData: oldAddressState })

    dispatch(action).unwrap()
      .then(() => {
        setOldSaved(true)
        dispatch(get_shipping_address(userInfo.id))
        dispatch(get_all_addresses(userInfo.id))
      })
      .catch(console.error)
  }

  const handleNewChange = e => {
    const { name, value } = e.target
    setNewAddress(s => ({ ...s, [name]: value }))
    setNewErrors(e => ({ ...e, [name]: '' }))
  }
  const handleSaveNew = e => {
    e.preventDefault()
    const errs = validateAddress(newAddress)
    if (Object.keys(errs).length) return setNewErrors(errs)

    dispatch(add_new_address({ customerId: userInfo.id, addressData: newAddress }))
      .unwrap()
      .then(() => {
        setShowNewForm(false)
        setNewAddress({ ...oldAddressState })
        dispatch(get_shipping_address(userInfo.id))
        dispatch(get_all_addresses(userInfo.id))
        setSelectedIndex(0)
      })
      .catch(console.error)
  }

  const handleEditChange = e => {
    const { name, value } = e.target
    setEditAddress(s => ({ ...s, [name]: value }))
    setEditErrors(e => ({ ...e, [name]: '' }))
  }
  const handleEditSave = e => {
    e.preventDefault()
    const errs = validateAddress(editAddress)
    if (Object.keys(errs).length) return setEditErrors(errs)

    dispatch(update_shipping_address({ addressId: editAddress._id, addressData: editAddress }))
      .unwrap()
      .then(() => {
        setShowEditForm(false)
        dispatch(get_shipping_address(userInfo.id))
        dispatch(get_all_addresses(userInfo.id))
      })
      .catch(console.error)
  }

  const handleSelectChange = e => {
    const idx = Number(e.target.value)
    setSelectedIndex(idx)
    const addr = allAddresses[idx]
    dispatch(set_selected_address({ customerId: userInfo.id, addressId: addr._id }))
  }

  const checkOneDay = () => {
    if (!currentAddress) return
    setShowOneDayOption(true)

    // build full phone or fallback
    const phone = currentAddress.countryCode && currentAddress.phone
      ? `${currentAddress.countryCode}${currentAddress.phone}`
      : '9999999999'

    dispatch(get_one_day_quote({
      city: currentAddress.city,
      area: currentAddress.area,
      phone
    }))
  }

  const placeOrderHandler = async () => {
  if (!currentAddress) return

  try {
    // 1) Create your order (with hyperDelivery flag if set)
    const orderId = await dispatch(
      create_order({
        price,
        products,
        shipping_fee,
        shippingInfo: currentAddress,
        userId: userInfo.id,
        hyperDelivery: useOneDay
      })
    ).unwrap()

    // 2) Persist shippingInfo + orderId
    localStorage.setItem(
      'shippingInfo',
      JSON.stringify({
        city: currentAddress.city,
        area: currentAddress.area,
        countryCode: currentAddress.countryCode,
        phone: currentAddress.phone
      })
    )
    localStorage.setItem('orderId', orderId)

    // 2a) Persist the chosen delivery data
    if (useOneDay) {
      // hyper-delivery
      localStorage.setItem('oneDayQuotePrice', oneDayPrice)
      localStorage.setItem('hyperDeliveryPrice', displayedOneDayPrice)

    } else {
      // 🔍 Re-fetch your standard quote right before placing order
      const {
        courier_company_id,
        courier_name,
        quotedPrice,
        transit_time,
        estimated_delivery_days
      } = await dispatch(
        get_standard_quote({
          pickup_pincode:   process.env.REACT_APP_WAREHOUSE_PINCODE,
          delivery_pincode: currentAddress.post,
          weight:           1
        })
      ).unwrap()

      console.log('📥 Standard quote fetched:', {
        courier_company_id,
        courier_name,
        quotedPrice,
        transit_time,
        estimated_delivery_days
      })

      // ⛔ Validate all five fields
      if (
        !courier_company_id ||
        !courier_name ||
        !quotedPrice ||
        !transit_time ||
        !estimated_delivery_days
      ) {
        console.error(
          '❌ Invalid standard-delivery quote, aborting placeOrder',
          { courier_company_id, courier_name, quotedPrice, transit_time, estimated_delivery_days }
        )
        return
      }

      // compute 30% markup
      const markupPrice = Math.ceil(quotedPrice * 1.3)

      // ✅ Persist everything
      localStorage.setItem('standardCourierCompanyId',      courier_company_id)
      localStorage.setItem('standardCourierName',           courier_name)
      localStorage.setItem('standardDeliveryPrice',         quotedPrice)       // raw
      localStorage.setItem('standardDeliveryPriceMarkedUp', markupPrice)       // marked-up
      localStorage.setItem('standardTransitTime',           transit_time)
      localStorage.setItem('standardEstimatedDeliveryDays', estimated_delivery_days)
    }

    // 3) Navigate to payment with correct total
    const deliveryCost = useOneDay
      ? displayedOneDayPrice
      : Math.ceil(
          // rebuild quotedPrice for markup in this scope
          Number(localStorage.getItem('standardDeliveryPrice') || 0) * 1.3
        )

    navigate('/payment', {
      state: {
        orderId,
        price: price + deliveryCost + packingCharge,
        items
      }
    })

  } catch (err) {
    console.error('Failed to place order →', err)
  }
}






  const renderCurrentAddressBlock = () => {
    if (!currentAddress) return null
    return (
      <div className="space-y-2">
        <h2 className="font-semibold">Deliver to {currentAddress.name}</h2>
        <p>
          <span className="bg-blue-200 px-2 py-0.5 rounded text-xs">
            {currentAddress.saveAs}
          </span>{' '}
          {currentAddress.address}, {currentAddress.province}, {currentAddress.city}, {currentAddress.area}
          <button
            onClick={() => { setShowEditForm(true); setEditAddress(currentAddress) }}
            className="ml-2 text-indigo-500 text-sm"
          >
            change
          </button>
        </p>
        <p className="text-sm">Email: {userInfo.email}</p>
        <button
          onClick={() => setShowNewForm(true)}
          className="text-indigo-500 text-sm"
        >
          Save Another Address
        </button>

        {allAddresses.length > 1 && (
          <div>
            <label className="block mb-1">Use Another Address:</label>
            <select
              value={selectedIndex}
              onChange={handleSelectChange}
              className="border px-2 py-1 rounded"
            >
              {allAddresses.map((addr, i) => (
                <option key={i} value={i}>
                  {addr.saveAs} – {addr.post}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
    )
  }

  const oldErrorMsg = Object.keys(oldErrors).length ? 'Fix the errors above' : ''
  const newErrorMsg = Object.keys(newErrors).length ? 'Fix the errors above' : ''
  const editErrorMsg = Object.keys(editErrors).length ? 'Fix the errors above' : ''

  return (
    <div>
      <Headers />

      {/* Banner */}
      <section className="relative h-56">
        <img src={orderjpg} alt="Order" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black opacity-50" />
        <div className="absolute inset-0 flex flex-col justify-center items-center text-white">
          <h2 className="text-3xl font-bold">Shop.my</h2>
          <div className="flex items-center space-x-2">
            <Link to="/">Home</Link>
            <MdOutlineKeyboardArrowRight />
            <span>Place Order</span>
          </div>
        </div>
      </section>

      {/* Main */}
      <section className="bg-gray-100 py-16">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex flex-wrap gap-8">
            {/* Left panel */}
            <div className="flex-1 min-w-[300px] space-y-6">
              <div className="bg-white p-6 rounded shadow">
                {!oldSaved && !showNewForm && !showEditForm ? (
                  <>
                    <h3 className="font-semibold mb-4">Shipping Information</h3>
                    <AddressForm
                      values={oldAddressState}
                      onChange={handleOldInput}
                      onSubmit={saveOldAddress}
                      errors={oldErrors}
                      formErrorMessage={oldErrorMsg}
                      buttonLabel="Save"
                    />
                  </>
                ) : showNewForm ? (
                  <>
                    <h3 className="font-semibold mb-4">Add a New Address</h3>
                    <AddressForm
                      values={newAddress}
                      onChange={handleNewChange}
                      onSubmit={handleSaveNew}
                      errors={newErrors}
                      formErrorMessage={newErrorMsg}
                      buttonLabel="Save"
                    />
                  </>
                ) : showEditForm ? (
                  <>
                    <h3 className="font-semibold mb-4">Edit Address</h3>
                    <AddressForm
                      values={editAddress}
                      onChange={handleEditChange}
                      onSubmit={handleEditSave}
                      errors={editErrors}
                      formErrorMessage={editErrorMsg}
                      buttonLabel="Update"
                    />
                  </>
                ) : (
                  renderCurrentAddressBlock()
                )}
              </div>

              {products.map((p, i) => (
                <div key={i} className="bg-white p-4 rounded shadow space-y-4">
                  <h4 className="font-medium">{p.shopName}</h4>
                  {p.products.map((pt, j) => (
                    <div key={j} className="flex items-center space-x-4">
                      <img
                        src={pt.productInfo.images[0]}
                        className="w-16 h-16 object-cover rounded"
                        alt=""
                      />
                      <div className="flex-1">
                        <p>{pt.productInfo.name}</p>
                        <p className="text-xs text-gray-500">{pt.productInfo.brand}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-orange-500">
                          ₹{pt.productInfo.price -
                            Math.floor((pt.productInfo.price * pt.productInfo.discount) / 100)}
                        </p>
                        <p className="text-xs line-through text-gray-400">
                          ₹{pt.productInfo.price}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>

            {/* Right panel */}
            <div className="w-80">
              <div className="bg-white p-6 rounded shadow space-y-4">
                <h3 className="font-semibold">Order Summary</h3>
                <div className="flex justify-between"><span>Items Total:</span><span>₹{price}</span></div>
                <div className="flex justify-between">
                  <span>Delivery Fee:</span>
                  <span>
                    {useOneDay
                      ? `₹${displayedOneDayPrice}`
                      : standardQuoteLoading
                        ? <span className="italic text-gray-500">Loading…</span>
                        : displayedStandardPrice != null
                          ? `₹${displayedStandardPrice}`     // ← now shows marked-up
                          : `₹${shipping_fee}`
                    }

                  </span>
                </div>
                <div className="text-xs text-gray-500">
                  {useOneDay
                    ? 'Est. delivery: Today'
                    : standardQuoteLoading
                      ? 'Checking…'
                      : transit_time
                        ? `Est. delivery: ${transit_time}`
                        : 'Est. delivery: N/A'
                  }
                </div>
                <div className="flex justify-between"><span>Packing:</span><span>₹{packingCharge}</span></div>

                {/* One-Day toggle */}
                <div className="pt-4">
                  {!oneDayPrice && !showOneDayOption ? (
                    <button
                      onClick={checkOneDay}
                      disabled={oneDayLoading}
                      className="w-full py-2 bg-blue-600 text-white rounded"
                    >
                      {oneDayLoading ? 'Checking…' : 'Check One-Day Delivery'}
                    </button>
                  ) : oneDayLoading ? (
                    <button disabled className="w-full py-2 bg-blue-300 text-white rounded">
                      Checking…
                    </button>
                  ) : showOneDayOption && oneDayPrice != null ? (
                    <div className="space-y-2">
                      <p>One-Day: ₹{displayedOneDayPrice}. Use it?</p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setUseOneDay(true); setShowOneDayOption(false)
                            //  dispatch(clearStandardQuote())
                            localStorage.setItem('oneDayQuotePrice', oneDayPrice)
                            localStorage.setItem('hyperDeliveryPrice', displayedOneDayPrice)

                          }}
                          className="flex-1 py-2 bg-green-600 text-white rounded"
                        >
                          Yes
                        </button>
                        <button
                          onClick={() => {
                            setUseOneDay(false)
                            setShowOneDayOption(false)
                            localStorage.removeItem('oneDayQuotePrice')
                            localStorage.removeItem('hyperDeliveryPrice')
                          }}
                          className="flex-1 py-2 bg-gray-300 rounded"
                        >
                          No
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>

                <div className="flex justify-between font-bold pt-4">
                  <span>Total</span>
                  <span>
                    ₹
                    {price
                      + packingCharge
                      + (useOneDay
                        ? displayedOneDayPrice
                        : (standardQuoteLoading || displayedStandardPrice == null
                          ? 0
                          : displayedStandardPrice
                        ))
                    }
                  </span>
                </div>

                <button
                  onClick={placeOrderHandler}
                  disabled={!currentAddress}
                  className={`w-full py-2 uppercase rounded ${currentAddress
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-300 text-gray-600 cursor-not-allowed'
                    }`}
                >
                  Place Order
                </button>
              </div>
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default Shipping
